import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../App";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Shield, FileText, CheckCircle2, Loader2, CreditCard, Building, Copy, AlertTriangle, ArrowRight } from "lucide-react";

export default function ServiceAgreementGate({ serviceId, onAgreementSigned, onCancel }) {
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [fullName, setFullName] = useState("");
  const [checks, setChecks] = useState({
    scope: false,
    nonRefundable: false,
    timelines: false,
    disputeWaiver: false,
  });
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  // Post-signing payment state
  const [step, setStep] = useState("agreement"); // "agreement" | "payment"
  const [invoiceData, setInvoiceData] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [bankTransferConfirmed, setBankTransferConfirmed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const checkRes = await apiClient.get(`/agreements/check/${user.id}`);
        if (checkRes.data.has_agreement) {
          setAlreadySigned(true);
          onAgreementSigned(checkRes.data.agreement_id);
          return;
        }
        const templateRes = await apiClient.get("/agreement-templates/active");
        setTemplate(templateRes.data);
      } catch (error) {
        toast.error("Failed to load service agreement");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.id, onAgreementSigned]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setScrolledToEnd(true);
    }
  };

  const allChecked = checks.scope && checks.nonRefundable && checks.timelines && checks.disputeWaiver;
  const canSign = allChecked && fullName.trim().length >= 2 && scrolledToEnd;

  const handleSign = async () => {
    if (!canSign) return;
    setSigning(true);
    try {
      const browserInfo = `${navigator.userAgent} | ${navigator.language} | ${screen.width}x${screen.height}`;
      const res = await apiClient.post("/agreements/sign", {
        template_id: template.id,
        full_legal_name: fullName.trim(),
        confirm_scope: true,
        confirm_non_refundable: true,
        confirm_timelines: true,
        confirm_dispute_waiver: true,
        browser_info: browserInfo,
        service_id: serviceId || null,
      });

      toast.success("Agreement signed successfully. A copy has been emailed to you.");

      // If invoice was auto-generated, show payment step
      if (res.data.invoice) {
        setInvoiceData(res.data.invoice);
        setStep("payment");
      } else {
        onAgreementSigned(res.data.agreement_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign agreement");
    } finally {
      setSigning(false);
    }
  };

  const handlePayByCard = async () => {
    if (!invoiceData) return;
    setPaymentProcessing(true);
    try {
      const res = await apiClient.post(`/invoices/${invoiceData.invoice_id}/pay`, {
        payment_method: "card",
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setPaymentProcessing(false);
    }
  };

  const handlePayByDeposit = async () => {
    if (!invoiceData) return;
    setPaymentProcessing(true);
    try {
      await apiClient.post(`/invoices/${invoiceData.invoice_id}/pay`, {
        payment_method: "bank_transfer",
        origin_url: window.location.origin,
      });
      setBankTransferConfirmed(true);
      toast.success("Direct deposit selected. Please complete your transfer and confirm through the helpdesk.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process");
    } finally {
      setPaymentProcessing(false);
    }
  };

  if (alreadySigned) return null;
  if (loading) return (
    <div className="flex items-center justify-center p-12">
      <Loader2 className="h-6 w-6 animate-spin text-sag-navy" />
    </div>
  );

  // ---- PAYMENT STEP (after agreement is signed) ----
  if (step === "payment" && invoiceData) {
    // Bank transfer confirmed — show confirmation + bank details
    if (bankTransferConfirmed) {
      return (
        <Card className="border-2 border-emerald-400 shadow-floating max-w-3xl mx-auto" data-testid="payment-deposit-confirmed">
          <CardHeader className="bg-emerald-700 text-white text-center py-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle2 className="h-6 w-6" />
              <CardTitle className="text-xl font-heading">Agreement Signed — Deposit Your Payment</CardTitle>
            </div>
            <p className="text-emerald-200 text-sm">Invoice {invoiceData.invoice_number} — ${invoiceData.total.toFixed(2)} {invoiceData.currency}</p>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <h3 className="font-heading font-semibold text-slate-900">Scotiabank Bahamas — Direct Deposit Details</h3>
              <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Bank</span>
                  <span className="text-sm font-semibold text-slate-900">ScotiaBank Bahamas</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Account Name</span>
                  <span className="text-sm font-semibold text-slate-900">Sebastian Alliance Group Ltd.</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold text-slate-900">171287</span>
                    <button
                      type="button"
                      className="p-1 hover:bg-slate-200 rounded"
                      onClick={() => { navigator.clipboard.writeText("171287"); toast.success("Account number copied"); }}
                      data-testid="copy-account-number-payment"
                    >
                      <Copy className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Branch Number</span>
                  <span className="text-sm font-mono font-bold text-slate-900">03465 (Carmichael)</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Amount Due</span>
                  <span className="text-lg font-bold text-emerald-700">${invoiceData.total.toFixed(2)} {invoiceData.currency}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Reference</span>
                  <span className="text-sm font-mono font-semibold text-slate-900">{invoiceData.invoice_number}</span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-amber-800 font-semibold">After depositing, confirm your payment</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Use <strong>{invoiceData.invoice_number}</strong> as your deposit reference.
                    Once deposited, submit a payment confirmation through the helpdesk so we can verify and activate your services promptly.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => onAgreementSigned(null)}
              className="w-full h-12 bg-sag-navy hover:bg-sag-navy-800 font-semibold"
              data-testid="continue-to-dashboard"
            >
              <span className="flex items-center gap-2">Continue to Dashboard <ArrowRight className="h-4 w-4" /></span>
            </Button>
          </CardContent>
        </Card>
      );
    }

    // Payment method selection
    return (
      <Card className="border-2 border-sag-navy shadow-floating max-w-3xl mx-auto" data-testid="payment-method-selection">
        <CardHeader className="bg-sag-navy text-white text-center py-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CheckCircle2 className="h-6 w-6" />
            <CardTitle className="text-xl font-heading">Agreement Signed — Payment Required</CardTitle>
          </div>
          <p className="text-blue-200 text-sm">Complete payment to activate your SLA services</p>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          {/* Invoice Summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="invoice-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-slate-900">Invoice Summary</h3>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-semibold">Pending Payment</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Invoice</span>
                <span className="font-semibold">{invoiceData.invoice_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">SLA Contract</span>
                <span className="font-semibold">{invoiceData.sla_title}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Billing Period</span>
                <span className="font-semibold">{invoiceData.billing_period}</span>
              </div>
              {invoiceData.items?.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-500">{item.description?.split("(")[0]?.trim()}</span>
                  <span className="font-semibold">${item.total.toFixed(2)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex justify-between text-base pt-1">
                <span className="font-bold text-slate-900">Total Due</span>
                <span className="font-bold text-lg text-sag-navy">${invoiceData.total.toFixed(2)} {invoiceData.currency}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Due Date</span>
                <span className="font-semibold text-red-600">{invoiceData.due_date}</span>
              </div>
            </div>
          </div>

          {/* Payment Options */}
          <div>
            <h3 className="font-heading font-bold text-slate-900 mb-3">Choose Payment Method</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Card Payment */}
              <button
                onClick={handlePayByCard}
                disabled={paymentProcessing}
                className="flex flex-col items-center gap-3 p-5 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group disabled:opacity-50"
                data-testid="pay-by-card"
              >
                <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                  <CreditCard className="h-6 w-6 text-blue-700" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-slate-900">Pay by Card</p>
                  <p className="text-xs text-slate-500 mt-1">Visa, Mastercard via Stripe</p>
                  <p className="text-xs text-emerald-600 font-semibold mt-1">Instant activation</p>
                </div>
                {paymentProcessing && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                )}
              </button>

              {/* Bank Transfer */}
              <button
                onClick={handlePayByDeposit}
                disabled={paymentProcessing}
                className="flex flex-col items-center gap-3 p-5 border-2 border-slate-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all group disabled:opacity-50"
                data-testid="pay-by-deposit"
              >
                <div className="p-3 bg-emerald-100 rounded-xl group-hover:bg-emerald-200 transition-colors">
                  <Building className="h-6 w-6 text-emerald-700" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-slate-900">Direct Deposit</p>
                  <p className="text-xs text-slate-500 mt-1">ScotiaBank Bahamas</p>
                  <p className="text-xs text-amber-600 font-semibold mt-1">Manual confirmation required</p>
                </div>
                {paymentProcessing && (
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                )}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---- AGREEMENT STEP ----
  return (
    <Card className="border-2 border-sag-navy shadow-floating max-w-3xl mx-auto" data-testid="agreement-gate">
      <CardHeader className="bg-sag-navy text-white text-center py-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Shield className="h-6 w-6" />
          <CardTitle className="text-xl font-heading">Service Agreement Required</CardTitle>
        </div>
        <p className="text-blue-200 text-sm">You must review and sign the service agreement before proceeding to payment</p>
      </CardHeader>
      <CardContent className="p-0">
        {/* Agreement Content */}
        <div
          className="p-6 max-h-[400px] overflow-y-auto border-b border-slate-200 prose prose-sm max-w-none"
          onScroll={handleScroll}
          data-testid="agreement-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template?.content || "") }}
        />

        {!scrolledToEnd && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-center text-sm text-amber-700" data-testid="scroll-notice">
            Please scroll to the bottom to read the full agreement before signing
          </div>
        )}

        {/* Checkboxes */}
        <div className="p-6 space-y-4 border-b border-slate-200 bg-slate-50" data-testid="acknowledgment-section">
          <h3 className="font-heading font-bold text-slate-900">Mandatory Acknowledgments</h3>

          <div className="flex items-start gap-3">
            <Checkbox
              id="scope"
              checked={checks.scope}
              onCheckedChange={(v) => setChecks({ ...checks, scope: !!v })}
              data-testid="check-scope"
            />
            <Label htmlFor="scope" className="text-sm leading-relaxed cursor-pointer">
              I confirm that I understand and accept the <strong>scope of services</strong> as described in this agreement.
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="nonRefundable"
              checked={checks.nonRefundable}
              onCheckedChange={(v) => setChecks({ ...checks, nonRefundable: !!v })}
              data-testid="check-non-refundable"
            />
            <Label htmlFor="nonRefundable" className="text-sm leading-relaxed cursor-pointer">
              I acknowledge that <strong>payments are non-refundable</strong> once services commence, a consultant is assigned, or resources are allocated.
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="timelines"
              checked={checks.timelines}
              onCheckedChange={(v) => setChecks({ ...checks, timelines: !!v })}
              data-testid="check-timelines"
            />
            <Label htmlFor="timelines" className="text-sm leading-relaxed cursor-pointer">
              I agree to the <strong>service delivery timelines</strong> provided and understand they are estimates unless otherwise specified.
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="disputeWaiver"
              checked={checks.disputeWaiver}
              onCheckedChange={(v) => setChecks({ ...checks, disputeWaiver: !!v })}
              data-testid="check-dispute-waiver"
            />
            <Label htmlFor="disputeWaiver" className="text-sm leading-relaxed cursor-pointer">
              I <strong>waive the right to initiate a chargeback</strong> without first following the mandatory internal dispute resolution process as outlined in this agreement.
            </Label>
          </div>
        </div>

        {/* Signature */}
        <div className="p-6 space-y-4" data-testid="signature-section">
          <h3 className="font-heading font-bold text-slate-900">Digital Signature</h3>
          <div className="space-y-2">
            <Label htmlFor="legalName" className="text-sm text-slate-700">Type your full legal name to sign this agreement</Label>
            <Input
              id="legalName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full legal name"
              className="text-lg h-12 border-2 border-slate-300 focus:border-sag-navy font-serif italic"
              data-testid="legal-name-input"
            />
          </div>

          {template && (
            <p className="text-xs text-slate-400">
              Agreement: {template.title} — Version {template.version}
            </p>
          )}

          <div className="flex gap-3">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1" data-testid="cancel-agreement">
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSign}
              disabled={!canSign || signing}
              className={`flex-1 h-12 font-semibold text-base ${canSign ? "bg-sag-navy hover:bg-sag-navy-800" : "bg-slate-300 cursor-not-allowed"}`}
              data-testid="sign-agreement-button"
            >
              {signing ? (
                <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Signing...</span>
              ) : (
                <span className="flex items-center gap-2"><FileText className="h-5 w-5" /> Sign Agreement & Continue to Payment</span>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
